import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Image, Form, Spinner } from "react-bootstrap";

import "bootstrap/dist/css/bootstrap.min.css";
import "./signin-email-otp.css";

import logo from "../../assets/images/MTDlogo.png";
import backarrow from "../../assets/images/backarrow.jpg";
import responsivebg from "../../assets/images/responsive-bg.png";

import { loginEmail, loginEmailOTP } from "../../actions/auth";
import { useCookies } from "../../hooks/useCookies";
import IconButton from "../userflow/components/card/IconButton";
import { handleUserNavigation } from "../../utils/handleUserRedirect";

export const SignInEmailOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCookie, setCookie } = useCookies();
  const intervalRef = useRef(null);

  const [timer, setTimer] = useState(120);
  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize email from cookies
  useEffect(() => {
    const savedEmail = getCookie("email");
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, [getCookie]);

  const startTimer = () => {
    clearInterval(intervalRef.current);
    setTimer(120);
    setCanResend(false);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          clearInterval(intervalRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const [code1, setCode1] = useState("");
  const [code2, setCode2] = useState("");
  const [code3, setCode3] = useState("");
  const [code4, setCode4] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const code1ref = useRef(null);
  const code2ref = useRef(null);
  const code3ref = useRef(null);
  const code4ref = useRef(null);

  const handleCodeChange = (e, setter, nextRef) => {
    const value = e.target.value;
    if (!isNaN(value) && value?.length <= 1) {
      setter(value);
      if (value?.length === 1 && nextRef) {
        nextRef.current.focus();
      }
    }
  };

  const handleKeyDown = (e, prevRef) => {
    if (e.key === "Backspace" && e.target.value === "" && prevRef) {
      prevRef.current.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = code1 + code2 + code3 + code4;

    if (code?.length !== 4) {
      setErrorMessage("*Invalid verification code.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const result = await loginEmail({
        email: email,
        otp: code,
        usingGoogle: false,
      });

      if (result.response) {
        setLoading(false);
        setErrorMessage("*" + (result.response?.data?.message || result.message));
        return;
      }

      if (!result.data || !result.data?.length) {
        setLoading(false);
        setErrorMessage("Please signup first.");
        return;
      }

      setCookie("token", result.token, 30);
      setCookie("userId", result.data[0].user_id, 30);
      setCookie("Approval", result.data[0].approval, 30);
      setCookie("verified", result.data[0]?.email_verified_at ? true : false, 30);

      if (result.data[0].approval === 20 || result.data[0].approval === 15) {
        setCookie("Name", result.data[0].first_name, 30);
      }

      await handleUserNavigation(result.data, navigate, setLoading, '');
    } catch (error) {
      setLoading(false);
      setErrorMessage("*" + (error.response?.data?.message || error.message || "An unexpected error occurred") + ".");
    }
  };

  const handleResendCode = async () => {
    if (!email || !email.includes("@")) {
      setErrorMessage("*Please re-enter email address.");
      return;
    }

    try {
      const response = await loginEmailOTP({ email });

      if (response.response) {
        setErrorMessage(response.response?.data?.message || response.message || "Failed to resend code");
        return;
      }

      startTimer();
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || error.message || "Failed to resend code");
    }
  };

  return (
    <div className="entercode-container">
      <div className="entercode-bg">
        <Image className="responsive-bg" src={responsivebg}></Image>
      </div>
      <Container className="entercode-main">
        <Container className="entercode-box">
          <Container className="logo-progressbar2">
            <Container className="logo-arrow2">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={() => window.history.back()}
              />
              <Image src={logo} alt="Logo" className="logo" />
            </Container>
          </Container>
          <Container className="entercode-text">
            <IconButton type="envelope" onClick={(e) => e.stopPropagation()} />
          </Container>
          <Form onSubmit={handleSubmit} className="entercode-form">
            <Container className="entercode-content">
              <div>
                <Form.Group controlId="formCode" className="entercode-form-group">
                  <Form.Label className="entercode-lable" htmlFor="code1">
                    Enter your verification code
                    <br />
                    <span>Please enter the 4-digit code sent to you at {email}.</span>
                  </Form.Label>
                  <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                    <Form.Control
                      id="code1"
                      className={`entercode-input ${errorMessage ? "error" : ""}`}
                      type="tel"
                      ref={code1ref}
                      value={code1}
                      onChange={(e) => handleCodeChange(e, setCode1, code2ref)}
                      onKeyDown={(e) => handleKeyDown(e, null)}
                      style={{ flex: 1 }}
                      aria-label="First digit of verification code"
                      maxLength={1}
                      autoComplete="off"
                    />
                    <Form.Control
                      className={`entercode-input ${errorMessage ? "error" : ""}`}
                      type="tel"
                      ref={code2ref}
                      value={code2}
                      onChange={(e) => handleCodeChange(e, setCode2, code3ref)}
                      onKeyDown={(e) => handleKeyDown(e, code1ref)}
                      style={{ flex: 1, marginLeft: "10px" }}
                      aria-label="Second digit of verification code"
                      maxLength={1}
                      autoComplete="off"
                    />
                    <Form.Control
                      className={`entercode-input ${errorMessage ? "error" : ""}`}
                      type="tel"
                      ref={code3ref}
                      value={code3}
                      onChange={(e) => handleCodeChange(e, setCode3, code4ref)}
                      onKeyDown={(e) => handleKeyDown(e, code2ref)}
                      style={{ flex: 1, marginLeft: "10px" }}
                      aria-label="Third digit of verification code"
                      maxLength={1}
                      autoComplete="off"
                    />
                    <Form.Control
                      className={`entercode-input ${errorMessage ? "error" : ""}`}
                      type="tel"
                      ref={code4ref}
                      value={code4}
                      onChange={(e) => handleCodeChange(e, setCode4, null)}
                      onKeyDown={(e) => handleKeyDown(e, code3ref)}
                      style={{ flex: 1, marginLeft: "10px" }}
                      aria-label="Fourth digit of verification code"
                      maxLength={1}
                      autoComplete="off"
                    />
                  </div>
                  {errorMessage && (
                    <Form.Text
                      className="text-danger error-message"
                      role="alert"
                      aria-live="polite"
                    >
                      {errorMessage}
                    </Form.Text>
                  )}
                </Form.Group>
                <div className="resend-timer">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (canResend) handleResendCode();
                    }}
                    className={!canResend ? "disabled" : ""}
                    style={{
                      pointerEvents: !canResend ? "none" : "auto",
                      color: !canResend ? "#cbcbcb" : "#5E5E5E",
                    }}
                  >
                    Resend code
                  </a>
                  <span>
                    {Math.floor(timer / 60)}:
                    {String(timer % 60).padStart(2, "0")} sec
                  </span>
                </div>
              </div>
              <button type="submit" className="global-next-btn" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" role="status" /> : "Next"}
              </button>
            </Container>
          </Form>
        </Container>
      </Container>
    </div>
  );
};
